import * as React from 'react'
import {ReactNode} from 'react'
import {EquipmentForParent} from './EquipmentForParent'
import {EquipmentsRepository} from './EquipmentsRepository'
import {Nullable} from '../../app/TypeHelpers'
import NewEquipment from './NewEquipment'
import {Messages} from '../../Messages'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../styledComponents/adminPage/Links'
import Equipment from './Equipment'
import AdminEquipmentForm, {EquipmentFormDataSet} from './AdminEquipmentForm'
import {OptionProps} from '../shared/forms/PPSelect'

interface Props {
    equipmentsRepo: EquipmentsRepository
    condominiumCd: string
}

interface State {
    isLoadCompleted: boolean
    parentEquipmentsMaster: OptionProps[]
    equipmentForParent: Nullable<EquipmentForParent[]>
    equipmentName: string
    explainText: string
    parentEquipmentId: Nullable<number>
    parentEquipmentName: Nullable<string>
    reservationCountMonthly: Nullable<number>
    reservationCountDaily: Nullable<number>
    lotteryEntryCountMonthly: Nullable<number>
}

class AdminRegisterEquipmentPage extends React.Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminRegisterEquipmentPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            isLoadCompleted: false,
            parentEquipmentsMaster: [],
            equipmentForParent: null,
            equipmentName: '',
            explainText: '',
            parentEquipmentId: null,
            parentEquipmentName: null,
            reservationCountMonthly: null,
            reservationCountDaily: null,
            lotteryEntryCountMonthly: null,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.requestResisterEquipment = this.requestResisterEquipment.bind(this)

        this.handleOnChange = this.handleOnChange.bind(this)
    }

    async loadEquipmentsForParent(): Promise<boolean> {
        const {equipmentsRepo, condominiumCd} = this.props

        const equipmentForParent: Nullable<EquipmentForParent[]> = await this.props.asyncCallFunction(
            equipmentsRepo.getEquipmentsForParent.bind(equipmentsRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )
        if (equipmentForParent) {
            this.setState({
                parentEquipmentsMaster: equipmentForParent.map((equipmentForParentName: EquipmentForParent) => {
                    return ({
                        label: equipmentForParentName.name,
                        value: `${equipmentForParentName.id}`,
                    })
                }),
                equipmentForParent: equipmentForParent,
            })
            return true
        }
        return false
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        await this.loadEquipmentsForParent()

        this.setState({
            isLoadCompleted: true,
        }, () => {
            this.props.eventEmitter.closeLoading()
        })
    }

    needToActivateRegisterButton(): boolean {
        return this.state.equipmentName !== ''
    }

    async requestResisterEquipment(): Promise<DetailsWithInformation<Equipment>> {
        return await this.props.equipmentsRepo.registerEquipment(
            this.props.condominiumCd,
            new NewEquipment(
                this.state.equipmentName,
                this.state.explainText,
                this.state.parentEquipmentId,
                this.state.reservationCountMonthly,
                this.state.reservationCountDaily,
                this.state.lotteryEntryCountMonthly,
            ))
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleRegisterOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()

        let equipmentAndInformation: Nullable<DetailsWithInformation<Equipment>> = null
        await this.props.eventEmitter.showLoading()
        try {
            equipmentAndInformation = await this.props.asyncCallFunction(
                this.requestResisterEquipment,
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('設備')},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        if (equipmentAndInformation) {
            const {condominiumCd} = this.props
            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/equipments/${equipmentAndInformation.details.id}`,
                {
                    state: {
                        initialMessage: {
                            title: equipmentAndInformation.information.title,
                            messageLevel: equipmentAndInformation.information.type,
                            detail: equipmentAndInformation.information.message,
                        },
                    },
                },
            )
        }
    }

    handleRegisterButtonClick(): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0012.errorLevel,
                message: Messages.IOPCC0012.toString('設備'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    handleOnChange(value: EquipmentFormDataSet): void {
        this.setState({...value})
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={!this.needToActivateRegisterButton()}
                        onClick={this.handleRegisterButtonClick}
                    >
                        登録
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/equipments`}
                    >
                        設備一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactNode {
        return (
            <>
                <PageMainArea>
                    {
                        this.state.isLoadCompleted &&
                        <AdminEquipmentForm
                            parentEquipmentsMaster={this.state.parentEquipmentsMaster}
                            equipmentForParent={this.state.equipmentForParent}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handleOnChange}
                        />
                    }
                </PageMainArea>

                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(AdminRegisterEquipmentPage)
